import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, BedDouble, Bath, Ruler, Phone, Mail } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '../components/ui/carousel';
import ImageWithSkeleton from '../components/ImageWithSkeleton';
import PhotoPlaceholder from '../components/PhotoPlaceholder';
import { APPLY_URL as APPLY_FORM_URL } from '../config';

const money = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const n = Math.round(parseFloat(val));
  if (Number.isNaN(n)) return null;
  return `$${n.toLocaleString('en-US')}`;
};

const rentValue = (listing) => {
  const range = listing.effectiveRent || listing.marketRent;
  if (!range || !range.min) return null;
  return Math.round(parseFloat(range.min));
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  if (d <= new Date()) return 'Available Now';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const Gallery = ({ photos, alt }) => {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const imgs = photos || [];

  useEffect(() => {
    if (!api) return undefined;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on('select', onSelect);
    return () => api.off('select', onSelect);
  }, [api]);

  if (imgs.length === 0) {
    return <PhotoPlaceholder aspectRatio="aspect-[4/3]" />;
  }

  return (
    <div data-testid="unit-gallery">
      <div className="relative">
        <Carousel setApi={setApi} opts={{ loop: imgs.length > 1 }} className="w-full">
          <CarouselContent>
            {imgs.map((src, i) => (
              <CarouselItem key={i}>
                <ImageWithSkeleton
                  src={src}
                  alt={`${alt} photo ${i + 1}`}
                  aspectRatio="aspect-[4/3]"
                  objectFit="object-contain"
                  bgClass="bg-gray-50"
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          {imgs.length > 1 && (
            <>
              <CarouselPrevious
                data-testid="gallery-prev"
                className="left-4 h-10 w-10 bg-white/90 border-none text-black hover:bg-white"
              />
              <CarouselNext
                data-testid="gallery-next"
                className="right-4 h-10 w-10 bg-white/90 border-none text-black hover:bg-white"
              />
              <div className="absolute bottom-4 right-4 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                {current + 1} / {imgs.length}
              </div>
            </>
          )}
        </Carousel>
      </div>

      {imgs.length > 1 && (
        <div className="flex gap-2 mt-4 overflow-x-auto hide-scrollbar pb-1">
          {imgs.map((src, i) => (
            <button
              key={i}
              onClick={() => api && api.scrollTo(i)}
              className={`flex-shrink-0 h-16 w-24 overflow-hidden border-2 bg-gray-50 transition-all ${
                current === i ? 'border-[#FF5722]' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
              aria-label={`View photo ${i + 1}`}
            >
              <img src={src} alt={`thumbnail ${i + 1}`} className="h-full w-full object-contain" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, value, label }) => (
  <div className="flex flex-col items-center text-center flex-1">
    <Icon className="h-6 w-6 text-[#FF5722] mb-2" />
    <span className="text-2xl font-bold text-black leading-none">{value}</span>
    <span className="text-sm text-gray-500 mt-1">{label}</span>
  </div>
);

const UnitDetail = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/listings/${id}`, {
          signal: controller.signal,
          cache: 'no-store',
        });
        if (res.status === 404) {
          setStatus('notfound');
          return;
        }
        if (!res.ok) {
          setStatus('error');
          return;
        }
        const data = await res.json();
        setListing(data);
        setStatus('ok');
      } catch (e) {
        setStatus('error');
      }
    };
    load();
    return () => controller.abort();
  }, [id]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-white container mx-auto px-4 py-28">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 aspect-[4/3] bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer bg-[length:200%_100%]" />
          <div className="space-y-4">
            <div className="h-6 w-1/3 bg-gray-200 rounded animate-pulse" />
            <div className="h-10 w-3/4 bg-gray-200 rounded animate-pulse" />
            <div className="h-24 w-full bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (status !== 'ok') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="unit-not-found">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-bold mb-4">
            {status === 'notfound' ? 'Unit no longer available' : 'Something went wrong'}
          </h1>
          <p className="text-gray-600 mb-8">
            {status === 'notfound'
              ? 'This apartment may have just been leased. Explore our other available floor plans.'
              : 'We had trouble loading this apartment. Please try again shortly.'}
          </p>
          <Link
            to="/floor-plans"
            className="inline-flex items-center bg-[#FF5722] text-white px-6 py-3 font-semibold uppercase tracking-wide hover:bg-[#e64a19] transition-colors rounded-md"
            data-testid="back-to-floorplans"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Floor Plans
          </Link>
        </div>
      </div>
    );
  }

  const unitLabel = (listing.name || '').split(' - ').pop() || listing.name;
  const rent = rentValue(listing);
  const beds = parseInt(listing.beds, 10) || 0;
  const perBed = rent && beds > 1 ? Math.round(rent / beds) : null;
  const sqft = listing.squareFeet && listing.squareFeet.min ? listing.squareFeet.min : null;
  const bedsText = beds === 0 ? 'Studio' : `${beds} Bedroom`;
  const title =
    listing.beds || listing.baths
      ? [bedsText, listing.baths ? `${listing.baths} Bath` : null].filter(Boolean).join(', ')
      : listing.name;

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="unit-detail-page">
      <div className="container mx-auto px-4 py-8">
        <Link
          to="/floor-plans"
          className="inline-flex items-center text-sm font-semibold text-gray-600 uppercase tracking-wide hover:text-[#FF5722] transition-colors mb-8"
          data-testid="back-link"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Floor Plans
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Gallery */}
          <div className="lg:col-span-2 animate-fade-in-up">
            <Gallery photos={listing.photos} alt={listing.name} />
          </div>

          {/* Info panel */}
          <div className="lg:sticky lg:top-28 self-start animate-fade-in-up animation-delay-100">
            <p className="text-sm font-bold text-[#FF5722] uppercase tracking-widest mb-2" data-testid="unit-label">
              Unit {unitLabel}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-black mb-6 leading-tight" data-testid="unit-title">
              {title}
            </h1>

            <div className="flex items-center justify-between gap-2 py-5 border-y border-gray-200 mb-6">
              <Stat icon={BedDouble} value={beds === 0 ? 'Studio' : listing.beds} label={beds === 0 ? 'Layout' : beds > 1 ? 'Bedrooms' : 'Bedroom'} />
              {listing.baths && <Stat icon={Bath} value={listing.baths} label={parseInt(listing.baths, 10) > 1 ? 'Bathrooms' : 'Bathroom'} />}
              {sqft && <Stat icon={Ruler} value={sqft} label="Sq Ft" />}
            </div>

            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Price</p>
                {rent ? (
                  <div data-testid="unit-price">
                    {perBed && <p className="text-2xl font-bold text-black leading-tight">{money(perBed)} <span className="text-base font-medium text-gray-500">/ bed</span></p>}
                    <p className={perBed ? 'text-base font-medium text-gray-600' : 'text-2xl font-bold text-black leading-tight'}>
                      {money(rent)} <span className="text-base font-medium text-gray-500">/ {perBed ? 'unit' : 'mo'}</span>
                    </p>
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-black">Contact Us</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500 font-medium mb-1">Available</p>
                <p className="text-lg font-semibold text-black" data-testid="unit-availability">
                  {formatDate(listing.availabilityDate) || 'Contact Us'}
                </p>
              </div>
            </div>

            {listing.deposit && money(listing.deposit) && (
              <p className="text-sm text-gray-500 mb-6">Security deposit: <span className="font-semibold text-black">{money(listing.deposit)}</span></p>
            )}

            <a
              href={APPLY_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="unit-apply-button"
              className="block w-full text-center bg-[#FF5722] text-white border-2 border-[#FF5722] hover:bg-white hover:text-[#FF5722] py-4 text-base font-semibold uppercase tracking-wide transition-all duration-200 rounded-md mb-3"
            >
              Apply Now
            </a>
            {listing.detailUrl && (
              <a
                href={listing.detailUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="unit-buildium-link"
                className="block text-center text-sm text-gray-500 hover:text-[#FF5722] transition-colors"
              >
                View official listing &amp; application
              </a>
            )}
          </div>
        </div>

        {/* Description */}
        {listing.description && (
          <div className="max-w-4xl mt-16 animate-fade-in-up">
            <h2 className="text-2xl font-bold text-black mb-4 uppercase tracking-wide">About This Home</h2>
            <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-line" data-testid="unit-description">
              {listing.description}
            </p>
          </div>
        )}
      </div>

      {/* Contact CTA */}
      <div className="bg-black text-white mt-16">
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            READY TO MAKE <span className="text-[#FF5722]">THE SUMMIT ON KING</span> YOUR HOME?
          </h2>
          <p className="text-gray-300 mb-8 text-lg">Contact us today to schedule a tour or ask a question.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {listing.phone && (
              <a
                href={`tel:${listing.phone.replace(/[^0-9]/g, '')}`}
                data-testid="unit-call-button"
                className="inline-flex items-center bg-[#FF5722] text-white px-8 py-4 font-semibold uppercase tracking-wide hover:bg-[#e64a19] transition-colors rounded-md"
              >
                <Phone className="mr-2 h-5 w-5" /> Call {listing.phone}
              </a>
            )}
            {listing.email && (
              <a
                href={`mailto:${listing.email}`}
                data-testid="unit-email-button"
                className="inline-flex items-center border-2 border-white text-white px-8 py-4 font-semibold uppercase tracking-wide hover:bg-white hover:text-black transition-colors rounded-md"
              >
                <Mail className="mr-2 h-5 w-5" /> Email Us
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitDetail;
